import { ChangeEvent, SyntheticEvent } from 'react';
import RichEditor from '@/src/components/common/RichEditor';
import {
  Autocomplete,
  AutocompleteValue,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import lightTheme from '@/src/styles/themes/lightTheme';
import AppUtils from '@/src/utils/AppUtils';
import {
  DYNAMIC_INPUT_TYPES_ENUM,
  IDynamicInputProp,
  IAttributeOptions,
  ILooseObject
} from '@/src/types/common.interface';
import ChipsInput, { ICustomSxProps } from '@/src/components/common/ChipsInput';

const TextInput = (props: IDynamicInputProp) => {
  const {
    id,
    variant = 'outlined',
    inputType,
    disabled,
    size = 'small',
    required,
    value = '',
    onChange,
    hasError,
    placeholder,
    helperText,
    onBlur,
    sx,
    maxLength
  } = props;

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue: string = event.target.value;
    if (typeof maxLength === 'number' && newValue.length > maxLength) {
      return;
    }
    onChange?.(newValue);
  };

  return (
    <TextField
      key={id}
      id={`${id}`}
      type={inputType}
      fullWidth
      variant={variant}
      disabled={disabled}
      size={size}
      placeholder={placeholder}
      required={required}
      value={value}
      onChange={handleChangeInput}
      sx={{ mb: 1, minWidth: 120, ...sx }}
      error={hasError}
      helperText={helperText}
      onBlur={onBlur}
      onKeyDown={(event) => AppUtils.blockInvalidInputChar(props.type, event)}
      autoComplete="off"
    />
  );
};

const LargeTextInput = (props: IDynamicInputProp) => {
  const {
    id,
    disabled,
    required,
    value = '',
    onChange,
    hasError,
    placeholder,
    helperText,
    onBlur,
    sx,
    maxLength,
    maxRows = 5,
    minWidth = 20
  } = props;

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue: string = event.target.value;
    if (typeof maxLength === 'number' && newValue.length > maxLength) {
      return;
    }
    onChange?.(newValue);
  };

  // Compute minWidth for the TextField
  let computedMinWidth: string | number = '20ch';
  if (typeof minWidth === 'number') {
    computedMinWidth = lightTheme.spacing(minWidth);
  }

  return (
    <TextField
      key={id}
      id={`${id}`}
      fullWidth
      multiline
      minRows={3}
      maxRows={maxRows}
      variant="outlined"
      disabled={disabled}
      required={required}
      value={value}
      placeholder={placeholder}
      onChange={handleChangeInput}
      onBlur={onBlur}
      error={hasError}
      helperText={helperText}
      sx={{
        mb: 1,
        minWidth: computedMinWidth,
        '& .MuiInputBase-root': {
          width: '100%'
        },
        ...sx
      }}
      onKeyDown={(event) => AppUtils.blockInvalidInputChar(props.type, event)}
      autoComplete="off"
    />
  );
};

const SelectInput = (props: IDynamicInputProp) => {
  const {
    id,
    sx,
    size = 'small',
    required,
    hasError,
    helperText,
    onBlur,
    disabled,
    placeholder,
    value = '',
    options = [],
    onChange,
    loading,
    disableClearable
  } = props;

  const currentOption =
    options.find((option: IAttributeOptions) => option?.value === value) ?? null;

  return (
    <Autocomplete
      key={id}
      id={`${id}`}
      options={options}
      fullWidth
      sx={{ mb: 1, minWidth: 120, ...sx }}
      size={size}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          placeholder={placeholder}
          error={hasError}
          helperText={helperText}
          onBlur={onBlur}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box key={`${key}-${option?.value}`} component="li" {...optionProps}>
            {option?.details ? (
              <Box display="flex" flexDirection="column">
                <Typography variant="subtitle2">{option?.label}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {option?.details}
                </Typography>
              </Box>
            ) : (
              option?.label
            )}
          </Box>
        );
      }}
      disabled={disabled}
      value={currentOption}
      onChange={(
        event: SyntheticEvent,
        selectedOption: AutocompleteValue<any, false, false, false>
      ): void => {
        onChange?.(selectedOption?.value ?? selectedOption ?? '');
      }}
      disableClearable={disableClearable}
    />
  );
};

const DynamicMultiChipsSelectInput = (props: IDynamicInputProp) => {
  const {
    id,
    sx,
    hasError,
    helperText,
    onBlur,
    disabled,
    value = [],
    options = [],
    onChange
  } = props;

  // Filter the valid options from the current value
  const validValues = (value as string[])?.filter((val: string | number) =>
    options?.some((opt: IAttributeOptions) => opt?.value === val)
  );

  // Find available options excluding the current values
  const availableOptions = options?.filter(
    (option: IAttributeOptions) => !validValues?.includes(option?.value as string)
  );

  const handleChangeInput = (option: IAttributeOptions) => {
    const updatedValues = [...validValues, option?.value];
    onChange?.(updatedValues);
  };

  const handleDelete = (option: string | number) => {
    const updatedValues = validValues?.filter((val: string | number) => val !== option);
    onChange?.(updatedValues);
  };

  const getLabelForValue = (val: string) => {
    const option = options?.find((opt: IAttributeOptions) => opt?.value === val);
    return option;
  };

  return (
    <Box key={id} display="flex" flexDirection="column" sx={{ rowGap: 2, ...sx }}>
      {/* Selected Options Section */}
      <Box>
        <Stack direction="row" spacing={1} rowGap={1} columnGap={1} flexWrap="wrap">
          {validValues.length > 0 ? (
            validValues.map((option) => (
              <Tooltip
                key={option}
                title={getLabelForValue(option)?.details ?? ''}
                placement="top"
                arrow
              >
                <span>
                  <Chip
                    label={getLabelForValue(option)?.label ?? option}
                    onDelete={(e) => {
                      onBlur?.(e);
                      handleDelete(option);
                    }}
                    color={hasError ? 'error' : 'default'}
                    disabled={disabled}
                  />
                </span>
              </Tooltip>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">
              No options selected
            </Typography>
          )}
        </Stack>
        {helperText && (
          <Typography variant="caption" color="error">
            {helperText}
          </Typography>
        )}
      </Box>

      {/* Available Options Section */}
      {!disabled && (
        <Box>
          <Stack direction="row" spacing={1} rowGap={1} columnGap={1} flexWrap="wrap">
            {availableOptions.length > 0 ? (
              availableOptions.map((option: IAttributeOptions) => (
                <Tooltip
                  key={option?.value as string}
                  title={option?.details ?? ''}
                  placement="top"
                  arrow
                >
                  <span>
                    <Chip
                      label={option.label}
                      onDelete={(e) => {
                        onBlur?.(e);
                        handleChangeInput(option);
                      }}
                      deleteIcon={<AddIcon />}
                      variant="filled"
                      color="info"
                    />
                  </span>
                </Tooltip>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                No available options
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const DynamicChipsTextInput = (props: IDynamicInputProp) => {
  const {
    id,
    sx,
    hasError,
    helperText,
    disabled,
    value = [],
    onChange,
    inputType,
    variant,
    size,
    label,
    placeholder,
    errorValues,
    required,
    chipsSeparator
  } = props;

  const handleInputChange = (newValue: string[]) => {
    const modifiedValues = [...newValue];

    if (newValue?.length > 0) {
      const currentValue = modifiedValues?.pop();
      const modifiedNewValues = currentValue?.split(chipsSeparator ?? ',');
      if (Array.isArray(modifiedNewValues) && modifiedNewValues?.length > 0) {
        modifiedNewValues?.forEach((value: string) => {
          const trimmedValue = value?.trim();
          if (trimmedValue?.length > 0 && !modifiedValues.includes(trimmedValue)) {
            modifiedValues?.push(trimmedValue);
          }
        });
      }
    }

    onChange?.(modifiedValues);
  };

  return (
    <ChipsInput
      type={inputType}
      id={String(id)}
      value={value}
      label={label ?? ''}
      variant={variant}
      disabled={disabled}
      onChange={handleInputChange}
      error={hasError}
      size={size}
      helperText={helperText ?? ''}
      isRequired={required ?? false}
      errorValues={errorValues}
      placeholder={placeholder}
      sx={sx as ICustomSxProps}
      chipsSeparator={chipsSeparator}
      isAutoCompleteOff
    />
  );
};

const MultiSelectInput = (props: IDynamicInputProp) => {
  const {
    options,
    id,
    value = [],
    disabled,
    sx,
    required,
    hasError,
    helperText,
    onBlur,
    loading,
    size = 'small',
    onChange,
    disableClearable,
    placeholder
  } = props;

  let currentOptions: ILooseObject[] = [];
  if (Array.isArray(value)) {
    currentOptions = (options ?? []).filter((option: ILooseObject) =>
      value?.includes?.(option.value)
    );
  } else if (value) {
    const currentOption = (options ?? []).find(
      (option: IAttributeOptions) => option.value === value
    );
    if (currentOption) {
      currentOptions = [currentOption];
    }
  }
  return (
    <Autocomplete
      key={id}
      id={`${id}`}
      options={options ?? []}
      fullWidth
      sx={{ mb: 2, minWidth: 120, ...sx }}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          error={hasError}
          helperText={helperText}
          onBlur={onBlur}
          size={size}
          placeholder={placeholder}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box key={`${key}-${option?.value}`} component="li" {...optionProps}>
            {option?.details ? (
              <Box display="flex" flexDirection="column">
                <Typography variant="subtitle2">{option?.label}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {option?.details}
                </Typography>
              </Box>
            ) : (
              option?.label
            )}
          </Box>
        );
      }}
      disabled={disabled}
      value={currentOptions}
      onChange={(
        event: SyntheticEvent,
        selectedOption: AutocompleteValue<any, false, false, false>
      ): void => {
        if (Array.isArray(selectedOption)) {
          const newValue = selectedOption.map((val) => val.value);
          onChange?.(newValue);
        } else {
          onChange?.([]);
        }
      }}
      multiple
      disableClearable={disableClearable}
    />
  );
};

const RichTextInput = (props: IDynamicInputProp) => {
  const {
    id,
    value = '',
    onChange,
    placeholder,
    hasError,
    helperText,
    richEditorProps,
    onBlur,
    maxHeight = 400
  } = props;

  return (
    <Box
      key={id}
      sx={{
        mb: 2,
        '& .ql-editor': { maxHeight, overflowY: 'auto' }
      }}
    >
      <RichEditor
        value={value as string}
        onChange={onChange as (value: string) => void}
        placeholder={placeholder}
        toolbarOptions={richEditorProps?.toolbarOptions}
        formats={richEditorProps?.formats}
        onBlur={onBlur}
      />
      {hasError && (
        <Typography color="error" variant="caption">
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

const CheckboxInput = (props: IDynamicInputProp) => {
  const options = props?.options ?? [];
  const isSingleSelect = props?.isCheckboxSingleSelect ?? false; // prop to determine single or multiple select

  const handleCheckboxChange = (optionValue: string) => (event: ChangeEvent<HTMLInputElement>) => {
    let newValue: string[] | string = [];

    if (isSingleSelect) {
      // Single selection mode: Only allow one checkbox to be selected at a time
      newValue = optionValue;
    } else if (Array.isArray(props?.value)) {
      // Multiple selection mode
      if (event.target.checked) {
        newValue = [...props?.value, optionValue];
      } else {
        newValue = props?.value?.filter((value: string) => value !== optionValue);
      }
    }
    props?.onChange?.(newValue);
  };

  return (
    <FormGroup row>
      {options?.map((option: IAttributeOptions) => (
        <FormControlLabel
          key={option?.label}
          control={
            <Checkbox
              checked={Array.isArray(props?.value) && props.value.includes(option?.value as string)}
              color="primary"
              onChange={handleCheckboxChange(option?.value as string)}
            />
          }
          label={option?.label}
        />
      ))}
    </FormGroup>
  );
};

const DynamicInput = (props: IDynamicInputProp): JSX.Element => {
  switch (props.type) {
    case DYNAMIC_INPUT_TYPES_ENUM.TEXT:
    case DYNAMIC_INPUT_TYPES_ENUM.INTEGER:
      return TextInput(props);
    case DYNAMIC_INPUT_TYPES_ENUM.SELECT:
      return SelectInput(props);
    case DYNAMIC_INPUT_TYPES_ENUM.DYNAMIC_MULTI_CHIPS_SELECT:
      return DynamicMultiChipsSelectInput(props);
    case DYNAMIC_INPUT_TYPES_ENUM.MULTI_SELECT:
      return MultiSelectInput(props);
    case DYNAMIC_INPUT_TYPES_ENUM.DYNAMIC_MULTI_CHIPS_TEXT:
      return DynamicChipsTextInput(props);
    case DYNAMIC_INPUT_TYPES_ENUM.HTML:
      return RichTextInput(props);
    case DYNAMIC_INPUT_TYPES_ENUM.LARGE_TEXT:
      return LargeTextInput(props);
    case DYNAMIC_INPUT_TYPES_ENUM.CHECKBOX:
      return CheckboxInput(props);
    default:
      return <></>;
  }
};
export default DynamicInput;
